import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCheck,
  Clock3,
  Plus,
  Sparkles,
  Ticket,
  TimerReset,
  UserCog,
} from "lucide-react";
import { TicketTable } from "../components/TicketTable";
import { ButtonLink, Card, PageHeader } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { defaultTicketPeriod } from "../lib/format";
import { Link } from "../lib/router";
import { firstName } from "../lib/utils";

const DEFAULT_PERIOD = defaultTicketPeriod();

function isClosed(status?: string) {
  const value =
    status?.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase() ?? "";
  return value.includes("final") || value.includes("resol");
}

export function DashboardPage() {
  const { session } = useAuth();
  const tickets = useQuery({
    queryKey: ["tickets", "dashboard", session?.codigoEmpresa],
    queryFn: () =>
      api.listTickets(session!.codigoEmpresa === 52 ? 0 : session!.codigoEmpresa, {
        page: 1,
        pageSize: 6,
        startDate: DEFAULT_PERIOD.startDate,
        endDate: DEFAULT_PERIOD.endDate,
      }),
    enabled: Boolean(session?.codigoEmpresa),
  });

  const visible = tickets.data?.itens ?? [];
  const inProgress = visible.filter((ticket) => !isClosed(ticket.ultimoStatus)).length;
  const completed = visible.filter((ticket) => isClosed(ticket.ultimoStatus)).length;
  const waiting = visible.filter((ticket) =>
    ticket.ultimoStatus?.toLowerCase().includes("aguard"),
  ).length;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Central de atendimento"
        title={`Olá, ${firstName(session?.login)}.`}
        description="Acompanhe o que está em andamento e abra uma nova solicitação quando precisar."
        actions={
          <ButtonLink to="/chamados/novo" className="hidden sm:inline-flex">
            <Plus className="size-4" />
            Novo chamado
          </ButtonLink>
        }
      />

      <section className="dashboard-hero">
        <div>
          <span className="hero-kicker">
            <Sparkles className="size-4" />
            Atendimento simplificado
          </span>
          <h2>Como podemos ajudar hoje?</h2>
          <p>
            Descreva o cenário com detalhes. A categoria certa ajuda nossa equipe
            a responder mais rápido.
          </p>
        </div>
        <div className="hero-actions">
          <Link to="/chamados/novo" className="hero-action">
            <span>
              <Plus className="size-6" />
            </span>
            <div>
              <strong>Abrir novo chamado</strong>
              <small>Leva menos de 2 minutos</small>
            </div>
            <ArrowRight className="ml-auto size-5" />
          </Link>
          {(session?.podeGerenciarUsuarios || session?.podeSolicitarAcesso) && (
            <Link to="/acessos" className="hero-action hero-action--secondary">
              <span>
                <UserCog className="size-6" />
              </span>
              <div>
                <strong>Solicitação de acesso</strong>
                <small>Criar ou desativar usuário</small>
              </div>
              <ArrowRight className="ml-auto size-5" />
            </Link>
          )}
        </div>
      </section>

      <section className="stat-grid">
        <Card className="stat-card stat-card--blue">
          <span className="stat-icon"><Ticket /></span>
          <div><small>Total de chamados</small><strong>{tickets.data?.totalDeItens ?? "—"}</strong></div>
          <p>Histórico disponível</p>
        </Card>
        <Card className="stat-card stat-card--brand">
          <span className="stat-icon"><Clock3 /></span>
          <div><small>Em andamento</small><strong>{tickets.isLoading ? "—" : inProgress}</strong></div>
          <p>Entre os mais recentes</p>
        </Card>
        <Card className="stat-card stat-card--amber">
          <span className="stat-icon"><TimerReset /></span>
          <div><small>Aguardando</small><strong>{tickets.isLoading ? "—" : waiting}</strong></div>
          <p>Entre os mais recentes</p>
        </Card>
        <Card className="stat-card stat-card--green">
          <span className="stat-icon"><CheckCheck /></span>
          <div><small>Finalizados</small><strong>{tickets.isLoading ? "—" : completed}</strong></div>
          <p>Entre os mais recentes</p>
        </Card>
      </section>

      <Card className="overflow-hidden">
        <div className="section-heading">
          <div>
            <h2>Chamados recentes</h2>
            <p>As últimas solicitações da sua empresa.</p>
          </div>
          <Link to="/chamados">
            Ver todos <ArrowRight className="size-4" />
          </Link>
        </div>
        {tickets.isError ? (
          <div className="inline-error">
            Não foi possível carregar os chamados agora.
            <button onClick={() => tickets.refetch()}>Tentar novamente</button>
          </div>
        ) : (
          <TicketTable
            tickets={tickets.data?.itens}
            loading={tickets.isLoading}
            compact
          />
        )}
      </Card>
    </div>
  );
}
